import React, {ReactElement} from 'react';
import {Box, Grid, Paper, SxProps, Theme, Typography} from '@mui/material';
import {Button} from '../../../components/Button/Button'; // Using main app's Button component
import {themeColors} from '../../../theme/Theme';
// import { Button } from '@saltus/ui'; // Commented out - @saltus dependency
// import { StatsInvitations, StatsOutstandingChase } from '@/graphql/graphql'; // Commented out - will need to define types

// Define types locally since we can't import from @saltus
type StatsInvitations = {
  overdue: number;
  upcoming: number;
};

type StatsOutstandingChase = {
  first: number;
  second: number;
  third: number;
};

type SectionCardProps = {
  chase?: StatsOutstandingChase;
  icon: ReactElement;
  invitations?: StatsInvitations;
  title: string;
  mainValue: number;
  mainLabel: string;
  subLabel?: string;
  buttonText: string;
  buttonAction: () => void; // Changed from Promise<void> to void for React Router navigation
  sx?: SxProps<Theme>;
};

function SectionCard({
  chase,
  icon,
  invitations,
  title,
  mainValue,
  mainLabel,
  subLabel,
  buttonText,
  buttonAction,
  sx = {},
}: SectionCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        marginBottom: 2,
        padding: 3,
        backgroundColor: themeColors.white,
        border: `1px solid ${themeColors.greyLight}`,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box display="flex" sx={{mb: 2}}>
        <Box
          sx={{
            mr: 1,
            color: themeColors.backgroundDark,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          component="p"
          sx={{
            fontWeight: 500,
            color: themeColors.backgroundDark,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Grid container spacing={2} flex={1} alignItems="center">
        <Grid item xs={12} sm={8}>
          <Box display="flex" flex={1} alignItems="center" sx={{mb: 1}}>
            <Typography
              variant="h2"
              component="span"
              sx={{
                marginRight: 2,
                fontWeight: 600,
                color: themeColors.backgroundDark,
              }}
            >
              {mainValue}
            </Typography>
            <Typography
              variant="h6"
              component="span"
              sx={{
                flex: 1,
                color: themeColors.grey,
                fontWeight: 500,
              }}
            >
              {mainLabel}
            </Typography>

            {invitations && invitations.overdue > 0 && (
              <>
                <Typography
                  variant="h2"
                  component="span"
                  sx={{
                    marginRight: 2,
                    fontWeight: 600,
                    color: themeColors.coral,
                  }}
                >
                  {invitations.overdue}
                </Typography>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    marginRight: 4,
                    color: themeColors.coral,
                    fontWeight: 500,
                  }}
                >
                  Overdue invitations
                </Typography>
              </>
            )}

            {chase && (
              <Box display="flex" alignItems="baseline" sx={{marginTop: {sm: 0, xs: 2}}}>
                {chase.first > 0 && (
                  <>
                    <Typography
                      variant="h2"
                      component="span"
                      sx={{
                        marginRight: 2,
                        fontWeight: 600,
                        color: themeColors.backgroundDark,
                      }}
                    >
                      {chase.first}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        marginRight: 4,
                        color: themeColors.backgroundDark,
                        fontWeight: 500,
                      }}
                    >
                      1st chase
                    </Typography>
                  </>
                )}
                {chase.second > 0 && (
                  <>
                    <Typography
                      variant="h2"
                      component="span"
                      sx={{
                        marginRight: 2,
                        fontWeight: 600,
                        color: themeColors.backgroundDark,
                      }}
                    >
                      {chase.second}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        marginRight: 4,
                        color: themeColors.backgroundDark,
                        fontWeight: 500,
                      }}
                    >
                      2nd chase
                    </Typography>
                  </>
                )}
                {chase.third > 0 && (
                  <>
                    <Typography
                      variant="h2"
                      component="span"
                      sx={{
                        marginRight: 2,
                        fontWeight: 600,
                        color: themeColors.backgroundDark,
                      }}
                    >
                      {chase.third}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        marginRight: 4,
                        color: themeColors.backgroundDark,
                        fontWeight: 500,
                      }}
                    >
                      3rd chase
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          {subLabel && (
            <Typography
              variant="body2"
              sx={{
                marginTop: 1,
                color: themeColors.muted,
                fontSize: '0.875rem',
              }}
            >
              {subLabel}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} container justifyContent="flex-start" sx={{}}>
          <Button
            color="primary"
            variant="contained"
            onClick={buttonAction}
            startIcon={icon}
            eventName={`section-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
            sx={{
              backgroundColor: themeColors.backgroundDark,
              '&:hover': {
                backgroundColor: themeColors.backgroundHighlight,
              },
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {buttonText}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default SectionCard;
